// Library Methods
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CloseIcon, DragIcon } from "src/assets/icons";

type DraggableExamCardType = {
  id: string;
  label: string;
  isDragMode: boolean;
  orderNumber: number;
  onRemoveCard: () => void;
};

const DraggableExamCard = ({
  id,
  label,
  isDragMode,
  orderNumber,
  onRemoveCard,
}: DraggableExamCardType) => {
  const isCompact = useMediaQuery("(max-width:500px)");

  const cardBackground = "#E2772E";

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const dragStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: isDragMode ? "grabbing" : "grab",
  };

  const card = (
    <Box
      id={id}
      key={id}
      display="flex"
      justifyContent="center"
      borderRadius="4px"
      p="4px"
      sx={{
        border: `1px solid ${cardBackground}`,
        backgroundColor: cardBackground,
        color: "#FFF",
        // Package order supports up to 5 cards; the container has an 8px
        // gap between cards (4 gaps at most), so subtract that from the
        // 1/5 share to keep 5 cards from overflowing the container. Keep
        // this dynamic share even in compact mode.
        flex: "0 0 calc((100% - 32px) / 5)",
        minWidth: 0,
        boxSizing: "border-box",
      }}
      style={{ ...dragStyle }}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2px",
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: 0,
            width: "100%",
          }}
        >
          {/* Hidden on small screens to save space; the whole card is
              still draggable via the listeners/attributes below. */}
          {!isCompact && (
            <Box color="#FFF" flexShrink={0}>
              <DragIcon height="16px" width="16px" />
            </Box>
          )}
          {isCompact ? (
            <Typography fontSize="12px" fontWeight={600} sx={{ flex: 1 }}>
              {orderNumber}
            </Typography>
          ) : (
            <Typography fontSize="12px" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {label}
            </Typography>
          )}
          <IconButton
            sx={{ color: "#FFF", padding: 0, flexShrink: 0 }}
            onClick={onRemoveCard}
          >
            <CloseIcon height="16px" width="16px" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip title={label} enterTouchDelay={0}>
      {card}
    </Tooltip>
  );
};

export default DraggableExamCard;
